// POR-T04 — Portable memory panel.
//
// Sits next to MemoryEditor in the Code side panel. Lets the user EXPORT their
// memories as a portable bundle (Save As → JSON) and IMPORT one back. Both
// actions need Electron IPC (file dialogs + fs); the panel renders nothing in
// the web build OR when the `memory.portable` flag is OFF. The Worker route
// (/api/memory/portable/export|import) is the authoritative DB read/write —
// Electron only handles the FILE side. Idempotent on import (server upserts by
// natural key); export is a snapshot of the current memories.

import { useCallback, useEffect, useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { apiFetch } from '../../../lib/api.js';
import { isElectron } from '../../../lib/isElectron.js';

const RUNTIME_NOT_ELECTRON = 'not-electron';
const STATUS_TIMEOUT_MS = 4000;

export default function PortableMemoryPanel() {
  const [enabled, setEnabled] = useState(null); // null=unknown / false=off / true=on
  const [status, setStatus] = useState(null); // {kind:'ok'|'err', message}
  const [busy, setBusy] = useState(false);

  // Resolve the gate once: flag from /api/memory/flags + Electron runtime check.
  useEffect(() => {
    let alive = true;
    (async () => {
      const electronOk = isElectron();
      if (!electronOk) { if (alive) setEnabled(RUNTIME_NOT_ELECTRON); return; }
      try {
        const res = await apiFetch('/api/memory/flags');
        if (!alive) return;
        if (res.ok && res.data?.portable === true) setEnabled(true);
        else setEnabled(false);
      } catch {
        if (alive) setEnabled(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Auto-clear status after a few seconds (non-error stays a bit; error sticky).
  useEffect(() => {
    if (!status || status.kind === 'err') return undefined;
    const t = setTimeout(() => setStatus(null), STATUS_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [status]);

  const onExport = useCallback(async () => {
    setBusy(true); setStatus(null);
    try {
      const got = await apiFetch('/api/memory/portable/export');
      if (!got.ok || !got.data) {
        setStatus({ kind: 'err', message: got.error || 'Falha ao exportar' });
        return;
      }
      const written = await window.apolo?.memory?.exportToFile?.(got.data);
      if (written?.ok) {
        setStatus({ kind: 'ok', message: `Exported ${got.data.count ?? 0} memories → ${written.path}` });
      } else if (written?.canceled) {
        setStatus(null);
      } else {
        setStatus({ kind: 'err', message: written?.error || 'Save canceled' });
      }
    } finally {
      setBusy(false);
    }
  }, []);

  const onImport = useCallback(async () => {
    setBusy(true); setStatus(null);
    try {
      const picked = await window.apolo?.memory?.importFromFile?.();
      if (!picked?.ok) {
        if (!picked?.canceled) setStatus({ kind: 'err', message: picked?.error || 'Import canceled' });
        return;
      }
      const res = await apiFetch('/api/memory/portable/import', {
        method: 'POST',
        body: JSON.stringify({ bundle: picked.bundle }),
      });
      if (res.ok && res.data) {
        const { inserted = 0, updated = 0, skipped = 0 } = res.data;
        setStatus({
          kind: 'ok',
          message: `Imported — ${inserted} inserted, ${updated} updated${skipped ? `, ${skipped} skipped` : ''}`,
        });
      } else {
        setStatus({ kind: 'err', message: res.error || 'Import failed' });
      }
    } finally {
      setBusy(false);
    }
  }, []);

  if (enabled !== true) return null;

  return (
    <div className="rounded-lg border border-white/5 bg-black/20 p-3" data-testid="portable-memory-panel">
      <div className="mb-2 text-xs uppercase tracking-wide text-zinc-400">Portable Memory</div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onExport}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-md bg-white/5 px-2.5 py-1.5 text-sm hover:bg-white/10 disabled:opacity-50"
        >
          <Download size={14} aria-hidden="true" /> Export
        </button>
        <button
          type="button"
          onClick={onImport}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-md bg-white/5 px-2.5 py-1.5 text-sm hover:bg-white/10 disabled:opacity-50"
        >
          <Upload size={14} aria-hidden="true" /> Import
        </button>
      </div>
      {status && (
        <div
          role={status.kind === 'err' ? 'alert' : 'status'}
          className={`mt-2 text-xs ${status.kind === 'err' ? 'text-red-400' : 'text-zinc-300'}`}
        >
          {status.message}
        </div>
      )}
    </div>
  );
}
